import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/shared/Sidebar';
import PageLoading from '../../../components/shared/PageLoading';
import Button from '../../../components/shared/Button';
import StatusBadge from '../../../components/shared/StatusBadge';

/**
 * USOKIE Platform Contracts Registry
 * Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
 */
const ContractList = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [expiringFilter, setExpiringFilter] = useState('');
  const [territories, setTerritories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchTerritories();
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [searchQuery, statusFilter, territoryFilter, expiringFilter, currentPage]);

  const fetchTerritories = async () => {
    try {
      const response = await fetch('/api/v1/territories');
      const data = await response.json();
      setTerritories(data);
    } catch (error) {
      console.error('Error fetching territories:', error);
    }
  };

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 25,
        ...(searchQuery && { q: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(territoryFilter && { territory_id: territoryFilter }),
        ...(expiringFilter && { expiring_within_days: expiringFilter })
      });

      const response = await fetch(`/api/v1/contracts?${params}`);
      const data = await response.json();

      setContracts(data.contracts || []);
      setTotalPages(data.meta?.total_pages || 1);
      setTotalCount(data.meta?.total_count || 0);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTerritoryChange = (e) => {
    setTerritoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleExpiringChange = (e) => {
    setExpiringFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && contracts.length === 0) {
    return <PageLoading message="Loading Contracts" />;
  }

  return (
    <div className="flex h-screen bg-black">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Document Header */}
          <div className="mb-8 pb-6 border-b border-[rgba(255,251,247,0.2)]">
            <div className="flex items-end justify-between">
              <div>
                <span
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  REGISTRY CTR.LIST
                </span>
                <h1 className="text-2xl font-bold text-[#fffbf7] tracking-[0.08em] uppercase">
                  Contracts Registry
                </h1>
              </div>
              <Button href="/contracts/new">
                + Create New Contract
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="text-xs text-[#a1c6a2] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                FILTERS
              </span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Search by contract number..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)]"
              />
              <select
                value={statusFilter}
                onChange={handleStatusChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_signature">Pending Signature</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="terminated">Terminated</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={territoryFilter}
                onChange={handleTerritoryChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">All Territories</option>
                {territories.map(territory => (
                  <option key={territory.id} value={territory.id}>{territory.name}</option>
                ))}
              </select>
              <select
                value={expiringFilter}
                onChange={handleExpiringChange}
                className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
              >
                <option value="">Expiring Within</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className="mb-6">
            <span
              className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em]"
              style={{ fontFamily: "'GT Pressura Mono', monospace" }}
            >
              SHOWING {contracts.length} OF {totalCount} CONTRACTS
            </span>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border border-[rgba(255,251,247,0.2)] border-t-[#a1c6a2] animate-spin" />
            </div>
          )}

          {/* Grid */}
          {!loading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {contracts.length === 0 ? (
                <div className="col-span-full border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] p-12 text-center">
                  <p className="text-sm text-[rgba(255,251,247,0.5)]">
                    No contracts found. Create your first one!
                  </p>
                </div>
              ) : (
                contracts.map(contract => (
                  <div
                    key={contract.id}
                    className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] hover:border-[rgba(255,251,247,0.2)] transition-colors"
                  >
                    {/* Card Header */}
                    <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                      <div className="flex items-center justify-between">
                        <a
                          href={`/contracts/${contract.id}`}
                          className="text-sm font-semibold text-[#fffbf7] hover:text-[#a1c6a2] transition-colors tracking-wide"
                          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                        >
                          {contract.contract_number}
                        </a>
                        <StatusBadge status={contract.status} />
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6">
                      {/* Contract Title */}
                      <h4 className="text-sm text-[rgba(255,251,247,0.8)] mb-4 pb-4 border-b border-[rgba(255,251,247,0.1)]">
                        {contract.title}
                      </h4>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Licensor
                          </span>
                          <span className="text-[rgba(255,251,247,0.6)]">
                            {contract.licensor?.name || '-'}
                          </span>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Licensees
                          </span>
                          <span className="text-[rgba(255,251,247,0.6)] text-right max-w-[60%]">
                            {contract.licensees && contract.licensees.length > 0
                              ? contract.licensees.map(l => l.name).join(', ')
                              : '-'}
                          </span>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Territory
                          </span>
                          <span className="text-[rgba(255,251,247,0.6)]">
                            {contract.territory?.name || '-'}
                          </span>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span
                            className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-wider uppercase"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            Period
                          </span>
                          <span
                            className="text-[rgba(255,251,247,0.6)]"
                            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                          >
                            {formatDate(contract.start_date)} — {formatDate(contract.end_date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="flex gap-2 px-6 py-4 border-t border-[rgba(255,251,247,0.1)] bg-black">
                      <Button href={`/contracts/${contract.id}`} variant="secondary" size="sm">
                        View
                      </Button>
                      <Button href={`/contracts/${contract.id}/edit`} variant="secondary" size="sm">
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-[rgba(255,251,247,0.1)]">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span
                className="text-[10px] text-[rgba(255,251,247,0.5)] tracking-wider"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ContractList;
